import React, { useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';

// Utils & Components
import { capitalizeFirstLetter } from '../../../components/functions';
import { DatoinLogo } from '../../../VerticalNavigation';
import { setProjectInfoKey } from '../../redux/FrameWorkReducer';
import '../ReusableCode/emlyMain.css';
import { getCurrentContextuserIdDetails } from './AccountUtility';
import ProfileMenu from './MenuComponent/ProfileMenu';
import NotificationPanel from './NotificationPanel';
import OrganizationDropDown from './OrganizationDropDown';
import './topLayerComponent.css';
import { IconBack } from '../../../UI-components/SvgIcons/Icons';

/**
 * Helper — resolves a color for the given status key.
 */
export const getColor = key => {
  switch (key) {
    case 'ABORTED':
    case 'FAILED':
    case 'QUEUED':
      return 'red';
    case 'RUNNING':
      return 'blue';
    case 'COMPLETED':
      return 'green';
    default:
      return 'gray';
  }
};

/**
 * Extracted small component to avoid anonymous render‑functions inside menu config.
 */
const OrganizationSelector = ({ errorPanel, showSwitch }) => (
  <div className="organizationDrop-down">
    <div className="OrgSelector">
      <OrganizationDropDown errorPanel={errorPanel} showSwitch={showSwitch} />
    </div>
  </div>
);

const TopLayerInternal = props => {
  // ────────────  STATE  ──────────── //
  const [isNotificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('Activities');
  const [removeIds, setRemoveIds] = useState([]);

  // ────────────  DERIVED DATA  ──────────── //
  const currentUserCtx = useMemo(
    () => getCurrentContextuserIdDetails(props.myUserAccountInfo && props.myUserAccountInfo.current_logged_user),
    [props.myUserAccountInfo && props.myUserAccountInfo.current_logged_user, props.myProfile]
  );

  const userInfo = props.userProfilePersisted && props.userProfilePersisted.userInformation;

  const initials = useMemo(() => {
    if (!userInfo) return 'U';
    const first = userInfo.name && userInfo.name[0] ? userInfo.name[0] : '';
    const last = userInfo.family_name && userInfo.family_name[0] ? userInfo.family_name[0] : '';
    return (first + last).toUpperCase();
  }, [userInfo]);

  // Build profile‑menu items just once when their deps change.
  const profileItems = useMemo(
    () => [
      {
        title: initials,
        root: true,
        label: userInfo && userInfo.name ? userInfo.name : 'User',
        submenu: [
          {
            title: 'Switch Org',
            type: 'link',
            submenu: [
              {
                type: 'component',
                title: (
                  <OrganizationSelector
                    errorPanel={props.errorPanel}
                    showSwitch={props.showSwitch}
                  />
                )
              }
            ]
          },
          { title: 'Profile', url: '/profile', type: 'link' },
          { title: 'Log out', type: 'link' }
        ]
      }
    ],
    [initials, userInfo, props.errorPanel, props.showSwitch]
  );

  // ────────────  HANDLERS  ──────────── //
  const navigateTo = useCallback(
    path => {
      props.history.push(path);
      props.dispatch(setProjectInfoKey(Date.now()));
    },
    [props.history, props.dispatch]
  );

  const profileItemsWithOnlyLogout = profileItems.map(function (item) {
    var copy = Object.assign({}, item);
    if (copy.submenu && Array.isArray(copy.submenu)) {
      copy.submenu = copy.submenu.filter(function (sub) {
        return sub.title === 'Log out';
      });
    } else {
      copy.submenu = [];
    }

    return copy;
  });

  const handlebrowserBack = () => {
    try {
      props.history.goBack();
    } catch (error) { }
  }

  // ────────────  RENDER  ──────────── //
  const theme = props.theme;
  if (!props.showSurvey) {
    return (
      <header data-theme={theme} className="topbarr">
        {/* Left side — logo & project title */}
        <div className="project-head">
          <div className="logo-div">
            <DatoinLogo curTheme={theme} />
          </div>

          <div title={props.planType === "LEARNER" ? 'Active Course' : 'Active Project'}
            onClick={() => {
              if (currentUserCtx.spaceId === 'undefined' || currentUserCtx.spaceId === null || !currentUserCtx.spaceId === undefined) {
                navigateTo('/projects');
              } else {
                navigateTo(
                  '/' + currentUserCtx.orgId + '/' + currentUserCtx.spaceId + '/projects/' + currentUserCtx.spaceId
                );
              }
            }}
            className="project-head-row projectHTitle"
          >
            <span className='browser-back' onClick={handlebrowserBack} title='Back'>
              {IconBack()}
            </span>
            <span className='txtHover' id='cu_pr_ct_name'>
              {currentUserCtx.spaceIdName ?
                props.planType === "LEARNER" && currentUserCtx.spaceIdName === "No Project Yet" ? 'No Course Yet' : capitalizeFirstLetter(currentUserCtx.spaceIdName) : ''
              }
            </span>
          </div>
        </div>

        {/* Right side — notifications & profile */}
        <div className="profile-icons-div">
          <div className="topicons">
            <NotificationPanel
              notificationpanel={isNotificationPanelOpen}
              setNotificationPanel={setNotificationPanelOpen}
              dispatch={props.dispatch}
              removeIds={removeIds}
              setRemoveIds={setRemoveIds}
              active={activeTab}
              disabled={!(props.myUserAccountInfo && props.myUserAccountInfo.current_logged_user)}
              tasks={props.tasks}
              setActive={setActiveTab}
              subscriptionUsage={props.subscriptionUsage}
              checkoutBilling={navigateTo}
              myProfile={props.myProfile}
            />

            <ProfileMenu
              disabled={false}
              menuItems={profileItems}
              dispatch={props.dispatch}
              showSwitch={props.showSwitch}
              checkUberDeployment={props.checkUberDeployment}
            />
          </div>
        </div>
      </header>
    );
  }

  else {
    return (
      <ProfileMenu
        disabled={false}
        menuItems={profileItemsWithOnlyLogout}
        dispatch={props.dispatch}
        showSwitch={props.showSwitch}
        checkUberDeployment={props.checkUberDeployment}
      />
    )
  }
};

TopLayerInternal.propTypes = {
  theme: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  tasks: PropTypes.array,
  subscriptionUsage: PropTypes.object,
  userProfilePersisted: PropTypes.object,
  myUserAccountInfo: PropTypes.object,
  myProfile: PropTypes.object,
  history: PropTypes.object.isRequired,
  showSwitch: PropTypes.bool,
  checkUberDeployment: PropTypes.object,
  errorPanel: PropTypes.bool
};

const mapStateToProps = state => ({
  api: state.api,
  myProfile: state.myProfile,
  checkUberDeployment: state.checkUberDeployment,
  notificationList: state.notification.saveNotifications,
  bgStatus: state.bgStatus,
  myApps: state.myApps,
  appItreationDetail: state.appItreationDetail,
  myUserAccountInfo: state.myUserAccountInfo,
  subscriptionUsage: state.subscriptionUsage,
  userProfilePersisted: state.userProfilePersisted,
});

export default withRouter(connect(mapStateToProps)(TopLayerInternal));
